import { FatalError, LintError } from '@rehaje/errors'
import { logErrorGroup, logWarnOnce } from '@rehaje/shared'

import { DevMode } from '../constants'
import { LoggerSilentRaiseWarning } from '../errors'

import type { CatchOptions, ErrorConstructor } from '../types'
import type { Callback } from '@rehaje/shared'

export class Exception {
  /**
   * 捕获到错误是否派发错误
   */
  static __r_catch_dispatch?: boolean

  /**
   * 捕获到的错误集合
   */
  static __r_catch_errors?: Error[]

  /**
   * 检查错误集合
   */
  __r_lint_errors: LintError[] = []

  /**
   * 根据错误构造器获取错误索引
   * @param constructor
   */
  __r_getErrorIndex<T extends Function>(constructor: T, cause?: any): number {
    return this.__r_lint_errors.findIndex(
      (e) =>
        e?.constructor === constructor &&
        (cause === undefined ||
          (typeof cause === 'function' ? cause(e.cause) : e?.cause === cause))
    )
  }

  /**
   * 捕获错误
   * @param callback 回调
   * @param options 捕获选项
   */
  catch(callback: Callback, options?: CatchOptions): Error[] {
    const prevCatchErrors = Exception.__r_catch_errors
    const prevCatchDispatch = Exception.__r_catch_dispatch
    const catchErrors: Error[] = []
    Exception.__r_catch_errors = catchErrors
    Exception.__r_catch_dispatch = options?.dispatch ?? prevCatchDispatch
    try {
      callback?.()
      if (!options?.isolated && catchErrors.length > 0)
        prevCatchErrors?.push(...catchErrors)
      return catchErrors
    } finally {
      Exception.__r_catch_errors = prevCatchErrors
      Exception.__r_catch_dispatch = prevCatchDispatch
    }
  }

  /**
   * 修复错误
   * @param constructor 检查错误构造器
   */
  fix<T extends LintError>(
    constructor: ErrorConstructor<T>,
    cause?: any
  ): T | undefined {
    const index = this.__r_getErrorIndex(constructor, cause)
    if (index !== -1)
      return this.__r_lint_errors.splice(index, 1)[0] as T | undefined
  }

  /**
   * 获取当前节点生产的特定错误信息
   * @param constructor 检查错误构造器
   */
  getLintError<T = LintError>(constructor: ErrorConstructor<T>): T | undefined {
    return this.__r_lint_errors.find((e) => constructor === e.constructor)
  }

  /**
   * 获取当前节点生产的错误信息
   */
  getLintErrors(): LintError[] {
    return this.__r_lint_errors
  }

  /**
   * 上报错误
   * @param error 错误
   * @param silent 是否静默
   */
  raise(error: Error, silent?: boolean): void {
    if (error instanceof FatalError) throw error
    if (DevMode) console.error(error)
    if (Exception.__r_catch_errors) Exception.__r_catch_errors.push(error)
    if (silent) logWarnOnce(new LoggerSilentRaiseWarning().message)
    else logErrorGroup(error)
    if (error instanceof LintError) {
      const index = this.__r_getErrorIndex(error.constructor, error.cause)
      if (index === -1) this.__r_lint_errors.push(error)
      else this.__r_lint_errors.splice(index, 1, error)
    }
  }
}
